import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Shield,
  MapPin,
  Clock,
  Award,
  Calendar,
  Users,
  CheckCircle,
  Phone
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResponderProfile = ({ user }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [responder, setResponder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API}/responders/${userId}/profile`);
        setResponder(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load profile");
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadProfile();
    }
  }, [userId]);

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "R";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="va-blue-bar h-2"></div>
        <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Responder Profile
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Profile Header Card */}
        <Card className="card-valor mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <Avatar className="w-24 h-24 border-4 border-primary/20">
                <AvatarImage src={responder.picture} />
                <AvatarFallback className="bg-primary text-white text-2xl">
                  {getInitials(responder.name)}
                </AvatarFallback>
              </Avatar>
              
              <div className="text-center sm:text-left flex-1">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    {responder.name}
                  </h1>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-3">
                  {responder.applicant_type === "civilian" ? (
                    <Badge variant="outline" className="border-secondary text-secondary">
                      <Users className="w-3 h-3 mr-1" />
                      Qualified Civilian
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-primary text-primary">
                      <Award className="w-3 h-3 mr-1" />
                      Veteran Responder
                    </Badge>
                  )}
                  {responder.is_available && (
                    <Badge className="bg-green-600">
                      <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
                      Available Now
                    </Badge>
                  )}
                </div>

                {/* Background Info */}
                <div className="text-muted-foreground text-sm">
                  {responder.applicant_type === "civilian" ? (
                    <>
                      {responder.professional_background && (
                        <span>{responder.professional_background}</span>
                      )}
                      {responder.years_of_experience > 0 && (
                        <span> • {responder.years_of_experience} years experience</span>
                      )}
                      {responder.organization && (
                        <span> • {responder.organization}</span>
                      )}
                    </>
                  ) : (
                    <>
                      {responder.military_branch && (
                        <span>{responder.military_branch}</span>
                      )}
                      {responder.years_of_service > 0 && (
                        <span> • {responder.years_of_service} years of service</span>
                      )}
                      {responder.discharge_status && (
                        <span> • {responder.discharge_status}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bio Section */}
        {responder.bio && (
          <Card className="card-valor mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Shield className="w-4 h-4 text-primary" />
                About
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground whitespace-pre-wrap">{responder.bio}</p>
            </CardContent>
          </Card>
        )}

        {/* Certifications */}
        {responder.certifications && responder.certifications.length > 0 && (
          <Card className="card-valor mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Award className="w-4 h-4 text-primary" />
                Certifications & Training
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {responder.certifications.map((cert, index) => (
                  <Badge key={index} variant="secondary" className="py-1.5 px-3">
                    <CheckCircle className="w-3 h-3 mr-1.5 text-green-600" />
                    {cert}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Availability */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Clock className="w-4 h-4 text-primary" />
              Availability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {responder.availability_days && responder.availability_days.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Available Days</p>
                <div className="flex flex-wrap gap-2">
                  {responder.availability_days.map((day, index) => (
                    <Badge key={index} variant="outline" className="py-1">
                      {day}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {responder.availability_hours && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Hours</p>
                <p className="text-foreground">{responder.availability_hours}</p>
              </div>
            )}

            {responder.zip_code && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>Located in {responder.zip_code}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Member Since */}
        {responder.member_since && (
          <Card className="card-valor">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Calendar className="w-4 h-4" />
                <span>Member since {new Date(responder.member_since).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long' 
                })}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Button - Only for veterans */}
        {user?.role === "veteran" && (
          <div className="mt-8">
            <Button 
              className="w-full h-14 text-lg"
              onClick={() => navigate("/dashboard")}
              data-testid="request-support-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Request Support
            </Button>
            <p className="text-center text-muted-foreground text-sm mt-2">
              You'll be connected with an available responder
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResponderProfile;
